import React, { useState } from "react";
import {
  Avatar, Badge, Box, Chip, Divider, IconButton, Popover, Stack, Typography, Button, Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNotifications } from "../../context/NotificationContext";
import { EmptyState } from "../ui";
import { openNotificationUrl } from "../../utils/openNotificationUrl";

const KIND_META = {
  "leave.submitted":                     { icon: <BeachAccessOutlinedIcon />,       color: "#F9BC06" },
  "leave.decision":                      { icon: <BeachAccessOutlinedIcon />,       color: "#39C645" },
  "leave.reminder":                      { icon: <BeachAccessOutlinedIcon />,       color: "#F9BC06" },
  "overtime.submitted":                  { icon: <AccessTimeOutlinedIcon />,        color: "#F9BC06" },
  "overtime.decision":                   { icon: <AccessTimeOutlinedIcon />,        color: "#39C645" },
  "attendance.regularization.submitted": { icon: <EditCalendarOutlinedIcon />,      color: "#F9BC06" },
  "attendance.regularization.decision":  { icon: <EditCalendarOutlinedIcon />,      color: "#39C645" },
  "reimbursement.submitted":             { icon: <ReceiptLongOutlinedIcon />,       color: "#F9BC06" },
  "reimbursement.decision":              { icon: <ReceiptLongOutlinedIcon />,       color: "#39C645" },
  "payroll.run.completed":               { icon: <PaymentsOutlinedIcon />,          color: "#6C50BF" },
  "payslip.available":                   { icon: <PaymentsOutlinedIcon />,          color: "#6C50BF" },
  "salary.revision.pending":             { icon: <PaymentsOutlinedIcon />,          color: "#F9BC06" },
  "salary.revision.decision":            { icon: <PaymentsOutlinedIcon />,          color: "#39C645" },
  "review.cycle.active":                 { icon: <AssessmentOutlinedIcon />,        color: "#6C50BF" },
  "review.cycle.closed":                 { icon: <AssessmentOutlinedIcon />,        color: "#64748B" },
  "review.assigned":                     { icon: <AssessmentOutlinedIcon />,        color: "#6C50BF" },
  "review.submitted":                    { icon: <AssessmentOutlinedIcon />,        color: "#39C645" },
  "review.reminder":                     { icon: <AssessmentOutlinedIcon />,        color: "#F9BC06" },
  "kudos.received":                      { icon: <EmojiEventsOutlinedIcon />,       color: "#F9BC06" },
  "goal.assigned":                       { icon: <FlagOutlinedIcon />,              color: "#6C50BF" },
  "checkin.scheduled":                   { icon: <HandshakeOutlinedIcon />,         color: "#5C85FF" },
  "pip.opened":                          { icon: <WarningAmberOutlinedIcon />,      color: "#EF4444" },
  "pip.closed":                          { icon: <HandshakeOutlinedIcon />,         color: "#39C645" },
  "survey.published":                    { icon: <PollOutlinedIcon />,              color: "#6C50BF" },
  "survey.reminder":                     { icon: <PollOutlinedIcon />,              color: "#F9BC06" },
  "announcement.posted":                 { icon: <CampaignOutlinedIcon />,          color: "#5C85FF" },
  "message.received":                    { icon: <MailOutlineOutlinedIcon />,       color: "#6C50BF" },
  "candidate.applied":                   { icon: <PersonAddAltOutlinedIcon />,      color: "#5C85FF" },
  "offer.issued":                        { icon: <DescriptionOutlinedIcon />,       color: "#39C645" },
  "onboarding.task.assigned":            { icon: <PersonAddAltOutlinedIcon />,      color: "#5C85FF" },
  "offboarding.task.assigned":           { icon: <PersonAddAltOutlinedIcon />,      color: "#64748B" },
  "letter.issued":                       { icon: <DescriptionOutlinedIcon />,       color: "#39C645" },
  "broadcast":                           { icon: <CampaignOutlinedIcon />,          color: "#5C85FF" },
  "info":                                { icon: <InfoOutlinedIcon />,              color: "#64748B" },
};

function iconFor(n) {
  const meta = KIND_META[n.kind] || KIND_META.info;
  return { ...meta };
}

function timeAgo(date) {
  const d = new Date(date);
  const ms = Date.now() - d.getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const day = Math.floor(h / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function NotificationBell() {
  const { items, unreadCount, markAllRead, markRead, dismiss, isRead } = useNotifications();
  const [anchor, setAnchor] = useState(null);
  const nav = useNavigate();
  const open = Boolean(anchor);

  const handleClick = (n) => {
    setAnchor(null);
    if (!isRead(n) && markRead) markRead(n._id);
    const dest = n.url || n.metadata?.meetingLink;
    if (dest) openNotificationUrl(dest, nav);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)} aria-label="notifications">
          <Badge
            badgeContent={unreadCount}
            color="error" max={99}
            sx={{ "& .MuiBadge-badge": { fontWeight: 700, fontSize: 10 } }}
          >
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 400, maxHeight: 540, borderRadius: 3 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, pb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Notifications</Typography>
            <Typography variant="caption" color="text.secondary">
              {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />} onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </Stack>
        <Divider />
        <Box sx={{ maxHeight: 440, overflowY: "auto" }}>
          {items.length === 0 ? (
            <Box sx={{ py: 5 }}>
              <EmptyState title="You're all caught up" description="New notifications will appear here" compact />
            </Box>
          ) : (
            items.map((n) => {
              const read = isRead(n);
              const { icon, color } = iconFor(n);
              return (
                <Box
                  key={n._id}
                  onClick={() => handleClick(n)}
                  sx={{
                    p: 1.5, cursor: n.url ? "pointer" : "default",
                    display: "flex", alignItems: "flex-start", gap: 1.5,
                    borderLeft: "3px solid",
                    borderLeftColor: read ? "transparent" : color,
                    bgcolor: read ? "transparent" : "action.hover",
                    borderBottom: "1px solid", borderBottomColor: "divider",
                    transition: "background 0.1s",
                    "&:hover": { bgcolor: n.url ? "#F4EDFA" : (read ? "transparent" : "action.hover") },
                  }}
                >
                  <Avatar sx={{
                    width: 34, height: 34, bgcolor: `${color}20`, color,
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                  }}>
                    {icon}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      {n.title && (
                        <Typography variant="body2" sx={{ fontWeight: read ? 500 : 700, fontSize: 13, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {n.title}
                        </Typography>
                      )}
                      {!read && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />}
                    </Stack>
                    {n.message && (
                      <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary", mt: 0.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {n.message}
                      </Typography>
                    )}
                    <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
                        {timeAgo(n.createdAt)}
                      </Typography>
                      {n.url && <Chip label="Open ↗" size="small" sx={{ height: 16, fontSize: 10, bgcolor: `${color}15`, color, fontWeight: 700 }} />}
                    </Stack>
                  </Box>
                  <IconButton
                    size="small" edge="end"
                    onClick={(e) => { e.stopPropagation(); dismiss(n._id); }}
                    sx={{ ml: 0.5, color: "text.disabled", "&:hover": { color: "error.main" } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
}
